import { useEffect, useState } from 'react';

/**
 * "More below" cue for the page column. When a page is taller than the
 * window, a soft fade along the bottom edge and a small "Scroll for more"
 * pill appear, so members know there's content below the fold. Both fade out
 * once the member starts scrolling (or reaches the end) and come back on the
 * next page. Floats over the page, so it never moves any content.
 */
export function ScrollHint({ routeKey }: { routeKey: string }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // The page scrolls on body (html/body overflow backstop) or the window.
    const scroller = () =>
      document.body.scrollHeight > document.body.clientHeight + 1 &&
      getComputedStyle(document.body).overflowY !== 'visible'
        ? document.body
        : (document.scrollingElement as HTMLElement) || document.documentElement;

    let raf = 0;
    const measure = () => {
      raf = 0;
      // Pages with an app-drawn diagram (the pill would sit on the drawing), or
      // with their own bottom action dock (the pill would hide behind it).
      if (document.querySelector('.sk-dg') || !!document.querySelector('.ilg-dock')?.getClientRects().length) {
        setShown(false);
        return;
      }
      const el = scroller();
      const top = el.scrollTop;
      const remaining = el.scrollHeight - el.clientHeight - top;
      // Only while at (or near) the top of a page with real content below.
      setShown(top < 120 && remaining > 160);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    // Content often arrives after the route renders; re-check as it grows.
    const t1 = setTimeout(schedule, 300);
    const t2 = setTimeout(schedule, 1200);
    const main = document.querySelector('.shell__main main');
    const ro = main ? new ResizeObserver(schedule) : null;
    if (main && ro) ro.observe(main);
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      ro?.disconnect();
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [routeKey]);

  const scrollDown = () => {
    const by = Math.round(window.innerHeight * 0.7);
    document.body.scrollBy?.({ top: by, behavior: 'smooth' });
    window.scrollBy({ top: by, behavior: 'smooth' });
  };

  return (
    <div className={`scrollhint${shown ? ' is-shown' : ''}`} aria-hidden={!shown}>
      <div className="scrollhint__fade" />
      <button type="button" className="scrollhint__pill" tabIndex={shown ? 0 : -1} onClick={scrollDown}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
        Scroll for more
      </button>
    </div>
  );
}
